<template>
    <div class="chart-container">
        <Chart
            v-if="hasData"
            type="bar"
            :data="chartData"
            :options="chartOptions"
            class="w-full h-full"
        />
        <div v-else class="chart-empty">Sem dados</div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import SafeChart from '@/components/SafeChart.vue';

const Chart = SafeChart;

const props = defineProps({
    rawData: { type: Object, default: () => ({}) },
    barColor: { type: [String, Array], default: '#10b981' } // Can be a single color or array of colors
});

const hasData = computed(() => {
    return props.rawData?.labels?.length > 0 && props.rawData?.datasets?.length > 0;
});

const chartData = computed(() => {
    if (!hasData.value) return {};
    
    const datasets = props.rawData.datasets.map(ds => {
        // If barColor is an array (like in Backlog), use it, otherwise use the single color with a gradient-like opacity array or just solid
        let bgColors = props.barColor;
        
        // Mockup shows multiple colors or gradients.
        // For a single color passed, we apply it. If it's an array, it applies per bar.
        return {
            ...ds,
            backgroundColor: bgColors,
            hoverBackgroundColor: bgColors,
            borderColor: 'transparent',
            hoverBorderColor: 'rgba(255, 255, 255, 0.8)',
            borderWidth: 0,
            hoverBorderWidth: 2,
            borderDash: [5, 5], // Dotted border
            hoverBorderDash: [5, 5],
            borderRadius: 6,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
        };
    });

    return {
        ...props.rawData,
        datasets
    };
});

const chartOptions = computed(() => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500, easing: 'easeOutQuart' },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(20, 20, 20, 0.85)',
                titleColor: '#ffffff',
                bodyColor: '#d1d5db',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                backdropFilter: 'blur(8px)',
                usePointStyle: true,
                boxPadding: 6,
            }
        },
        scales: {
            x: {
                grid: { display: false, drawBorder: false },
                ticks: { color: '#6b7280', font: { size: 11, family: 'Inter, sans-serif' } },
                border: { display: false }
            },
            y: {
                grid: { color: 'rgba(255, 255, 255, 0.05)', drawBorder: false },
                ticks: { color: '#6b7280', font: { size: 11, family: 'Inter, sans-serif' }, padding: 10 },
                border: { display: false },
                beginAtZero: true
            }
        }
    };
});
</script>

<style scoped>
.chart-container {
    height: 220px;
    width: 100%;
    position: relative;
}
.chart-empty {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    font-size: 0.9rem;
}
</style>
